/**
 * dotPlotAxisScale - axis domain/tick resolution for the Performance Analytics dot plot
 *
 * SE-6293: the scatter plot's axes were pinned to a fixed `[0, 100]` domain, so any
 * player whose rating exceeded 100 (the reported case: Lamine Yamal's Dribbling at
 * 147) plotted outside the drawable area and its dot was never painted.
 *
 * The scale resolved here keeps the canonical 0–100 grid for ordinary data and only
 * grows when the data actually needs it, so the common case is pixel-identical to
 * the pre-fix chart. Both the web (Victory SVG) and native (victory-native / Skia)
 * views resolve their axes through this module, so the two platforms cannot drift.
 */

/**
 * Baseline axis maximum. Ratings are nominally 0–100, so an axis never shrinks
 * below this — a squad of low-rated players still reads against the familiar grid
 * rather than being stretched across an arbitrary smaller range.
 */
const BASE_AXIS_MAX = 100;

/** The pre-fix tick set, reproduced whenever the data fits inside 0–100. */
const BASE_TICK_VALUES: readonly number[] = [0, 25, 50, 75, 100];

/**
 * Tick-spacing ladder, tried in order. Coarser steps keep the label count readable
 * once the domain has to grow well past the nominal 100 ceiling.
 */
const TICK_STEPS: readonly number[] = [25, 50, 100, 250, 500, 1000];

/** Most intervals (i.e. ticks minus one) an axis may show before the step coarsens. */
const MAX_TICK_INTERVALS = 6;

/**
 * A resolved axis scale: the domain's upper bound and the tick values that label it.
 */
export interface DotPlotAxisScale {
  /** Upper bound of the axis domain; the lower bound is always 0. */
  readonly max: number;
  /**
   * Tick positions from 0 to `max` inclusive, evenly spaced.
   * Mutable by design — Victory's `tickValues` prop is typed `number[]`.
   */
  readonly tickValues: number[];
}

/**
 * The unchanged 0–100 scale, returned whenever the data fits inside it.
 * @returns A fresh baseline scale
 * @remarks Built per call rather than shared, so a chart library that sorts or
 * otherwise mutates the `tickValues` array it is handed cannot corrupt the default.
 */
const baseScale = (): DotPlotAxisScale => ({
  max: BASE_AXIS_MAX,
  tickValues: [...BASE_TICK_VALUES],
});

/**
 * Greatest finite value in a series, ignoring nulls, undefined and NaN/Infinity.
 * @param values - Raw coordinate values for one axis
 * @returns The largest usable value, or 0 when the series has none
 */
const greatestFinite = (
  values: readonly (number | null | undefined)[]
): number =>
  values.reduce<number>(
    (highest, value) =>
      typeof value === "number" && Number.isFinite(value) && value > highest
        ? value
        : highest,
    0
  );

/**
 * Smallest tick step that keeps the tick count within `MAX_TICK_INTERVALS`.
 * @param max - Greatest value that must fit inside the domain
 * @returns A tick step, derived from `max` itself when the ladder is exhausted
 * @remarks The derived fallback (a power of ten) guarantees a bounded tick count
 * for pathological data rather than generating an unbounded tick array.
 */
const resolveTickStep = (max: number): number => {
  const laddered = TICK_STEPS.find(
    step => Math.floor(max / step) + 1 <= MAX_TICK_INTERVALS
  );
  if (laddered !== undefined) return laddered;
  return 10 ** Math.ceil(Math.log10(max / MAX_TICK_INTERVALS));
};

/**
 * Resolve the domain and tick values for one dot plot axis.
 * @param values - Coordinate values plotted on this axis (nulls are ignored)
 * @returns The axis upper bound and its evenly spaced tick values
 * @remarks
 * When every value fits inside 0–100 the result is the pre-SE-6293 scale exactly:
 * `max: 100`, ticks `[0, 25, 50, 75, 100]`.
 *
 * When a value exceeds 100 the domain grows to the next tick boundary *strictly
 * above* that value, so the outlier's dot is never half-clipped by sitting exactly
 * on the axis edge — which is the whole point of the fix. Tick spacing stays on the
 * 25-step grid until the label count would exceed `MAX_TICK_INTERVALS`, so a
 * moderate outlier keeps the familiar 0/25/50/75/100 gridlines in place and merely
 * extends past them.
 */
export const resolveDotPlotAxisScale = (
  values: readonly (number | null | undefined)[]
): DotPlotAxisScale => {
  const max = greatestFinite(values);
  if (max <= BASE_AXIS_MAX) return baseScale();

  const step = resolveTickStep(max);
  const intervals = Math.floor(max / step) + 1;
  const top = intervals * step;

  return {
    max: top,
    tickValues: Array.from({ length: intervals + 1 }, (_, i) => i * step),
  };
};

/**
 * A player coordinate pair as supplied to the dot plot views.
 */
interface DotPlotPoint {
  readonly x: number | null;
  readonly y: number | null;
}

/**
 * Both axis scales for a dot plot, resolved from its full player set.
 */
export interface DotPlotAxisScales {
  /** Scale for the horizontal (X) metric */
  readonly x: DotPlotAxisScale;
  /** Scale for the vertical (Y) metric */
  readonly y: DotPlotAxisScale;
}

/**
 * Resolve independent X and Y scales for a dot plot from its plotted players.
 * @param players - Every player rendered on the chart, including the current player
 * @returns Independently resolved X and Y axis scales
 * @remarks
 * The axes scale independently because each one shows a metric the user picks
 * separately from its own dropdown; tying them together would compress one axis to
 * accommodate an outlier belonging to the other. Every series is included —
 * background, comparison and current player alike — so the current player's dot is
 * inside the domain no matter which series carries the extreme value.
 */
export const resolveDotPlotAxisScales = (
  players: readonly DotPlotPoint[]
): DotPlotAxisScales => ({
  x: resolveDotPlotAxisScale(players.map(p => p.x)),
  y: resolveDotPlotAxisScale(players.map(p => p.y)),
});
